import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  ThemeProvider,
  createTheme,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ButtonGroup,
  Divider,
  Toolbar,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { generateInvoicePDF } from "./pdfGenerator";
import { useUserContext } from './context/UserContext';
import { doc, getDoc, updateDoc, increment, collection, addDoc, Timestamp, runTransaction } from "firebase/firestore";
import { db } from "../firebase.config";
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import {
  Create,
  Close,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { InvoiceContext } from "./context/InvoiceContext";
import SelectCreditNotesModal from "./SelectCreditNotesModal";

// --- Helper Components ---
const SignatureUploadDialog = ({ open, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [imageSrc, setImageSrc] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    // Validation: 5MB limit
    if (file.size > 5 * 1024 * 1024) {
      setError('File is too large. Please select an image under 5MB.');
      setImageSrc(null);
      return;
    }

    setError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (!imageSrc) {
      onClose();
      return;
    }

    // Image compression and resizing logic
    const img = new Image();
    img.src = imageSrc;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set max dimensions for the signature image to optimize it
      const maxWidth = 400;
      const maxHeight = 200;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      
      // Get compressed image data URL as a PNG
      const compressedImageData = canvas.toDataURL('image/png', 0.9);

      onSave(compressedImageData);
      handleClose(); // Use handleClose to ensure state is reset
    };
    img.onerror = () => {
        console.error('Failed to load image');
    }
  };
  
  
  const handleClose = () => {
    // Reset state when the dialog is closed
    setImageSrc(null);
    setName('');
    setError('');
    if (fileInputRef.current) {
        fileInputRef.current.value = "";
    }
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Upload Signature Image</DialogTitle>
      <DialogContent>
        <Button variant="contained" component="label" fullWidth sx={{ mb: 2 }}>
          Select Image
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="image/png, image/jpeg"
            onChange={handleFileChange}
          />
        </Button>
        {error && <Typography color="error" variant="body2" sx={{ mb: 2, textAlign: 'center' }}>{error}</Typography>}
        {imageSrc && (
          <Box sx={{ border: '1px dashed #ccc', borderRadius: 1, mb: 2, p: 1, textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview:</Typography>
            <img 
              src={imageSrc} 
              alt="Signature Preview" 
              style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} 
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!imageSrc}>Save Signature</Button>
      </DialogActions>
    </Dialog>
  );
};


const theme = createTheme({
  palette: {
    primary: { main: "#3f51b5" },
    secondary: { main: "#dc004e" },
  },
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    h4: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
});

const formatCurrency = (amount, symbol = "₹") => {
  const num = parseFloat(amount);
  if (isNaN(num)) {
    return `${symbol}0.00`;
  }
  return `${symbol}${num.toFixed(2)}`;
};

// Helper function to convert editor HTML to plain text for PDF
const htmlToPlainText = (html) => {
    if (!html) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
};

const formatTableCurrency = (value, symbol) => {
  const numValue = parseFloat(value).toFixed(2);
  
  // Use text that works with jsPDF's default font
  if (symbol === '₹' || symbol === '\u20B9') {
    return `Rs. ${numValue}`;
  }
  if (symbol === '$') {
    return `$ ${numValue}`;
  }
  if (symbol === '€') {
    return `EUR ${numValue}`;
  }
  return `${symbol} ${numValue}`;
};
// --- Main InTemplate Component ---

const InTemplate = () => {
    const location = useLocation();
    // MODIFIED: Use invoice-specific variables from context
    const {
        isAdmin,
        userProfile,
        invoiceLogo,
        invoiceBanner,
        invoiceBusinessName,
        invoiceBusinessAddress,
        invoiceDefaultTermsAndConditions,
        invoiceDefaultAdditionalNotes,
        invoiceDefaultAuthorizedSignature,
        invoiceDefaultAuthorizedSignatureName,
        userId,
        invoiceValidityDays,
        invoiceCurrency,
        invoiceFooter,
        invoiceIdPrefix, setInvoiceIdPrefix,
        invoicePrefixSetAt, setInvoicePrefixSetAt,
        invoiceCustomCounter,
        showInvoiceGST,
      } = useUserContext();

  // TAX INVOICE / PROFORMA INVOICE logic
  const [invoiceType, setInvoiceType] = useState("tax");
  const handleInvoiceTypeChange = (event, newType) => {
    if (newType !== null) setInvoiceType(newType);
  };

  // Helper function to show notifications
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') return;
    setNotification({ ...notification, open: false });
  };
  
    const getCurrencySymbol = () => {
        let symbol = '₹';
        if (typeof invoiceCurrency === 'object' && invoiceCurrency !== null && typeof invoiceCurrency.symbol === 'string' && invoiceCurrency.symbol.length > 0) {
            symbol = invoiceCurrency.symbol;
        } else if (typeof invoiceCurrency === 'string' && invoiceCurrency.length > 0) {
            symbol = invoiceCurrency;
        }
        if (!isNaN(parseFloat(symbol)) && isFinite(symbol)) {
            return '$';
        }
        return symbol;
    };
    const currencySymbol = getCurrencySymbol();
  
  const navigate = useNavigate();
  const { selectedItems, draft, setDraft, clearSelectedItems, selectedCreditNotes, setSelectedCreditNotes } = useContext(InvoiceContext);
  
  const [showCreditNotesModal, setShowCreditNotesModal] = useState(false);
  const [totalCreditNoteAmount, setTotalCreditNoteAmount] = useState(0);
  
  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [billTo, setBillTo] = useState({ name: "", address: "" });
  const [shipTo, setShipTo] = useState({ name: "", address: "" });
  
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [validUntil, setValidUntil] = useState('');
  const [items, setItems] = useState([
    { description: "", unitPrice: 0, quantity: 0, discount: 0, tax: 0, total: 0, isLocked: false, sourceId: null },
  ]);
  const getTodayDate = () => new Date().toISOString().split('T')[0];
  
  // Handle invoice date change with calendar restriction for tax invoices
  const handleInvoiceDateChange = (e) => {
    if (invoiceType === 'tax') {
      // For tax invoices, only allow today's date
      const todayDate = getTodayDate();
      setInvoiceDate(todayDate);
    } else {
      // For proforma invoices, allow any date
      setInvoiceDate(e.target.value);
    }
    setHasUnsavedChanges(true);
  };

  const [additionalNotes, setAdditionalNotes] = useState(invoiceDefaultAdditionalNotes || "");
  const [termsAndConditions, setTermsAndConditions] = useState(invoiceDefaultTermsAndConditions || "");
  const [authorizedSignature, setAuthorizedSignature] = useState(invoiceDefaultAuthorizedSignature || "");
  const [authorizedSignatureName, setAuthorizedSignatureName] = useState(invoiceDefaultAuthorizedSignatureName || "");
  
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [InvoiceId, setInvoiceId] = useState(null);
  const [templateId, setTemplateId] = useState(6);

  const [coveringLetter, setCoveringLetter] = useState("");
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [currentSignatureType, setCurrentSignatureType] = useState("");
  const [customerSignature, setCustomerSignature] = useState("");
  const [customerSignatureName, setCustomerSignatureName] = useState("");
  // REMOVED: customFooter state - now using invoiceFooter from context
  const contentRef = useRef(null);
  const editorRef = useRef(null);
  
  // --- CHANGE 2: REPLACED DRAFT RESTORATION LOGIC ---
  // This combined hook handles all draft restoration scenarios to persist form state.
  useEffect(() => {
    // Priority 1: Editing a specific invoice passed via navigation state.
    if (location.state && location.state.draft) {
      const draftData = location.state.draft;
      setInvoiceNumber(draftData.invoiceNumber || draftData.invoiceId || "YYYYMMDD-###");
      setBillTo(draftData.billTo || { name: "", address: "" });
      setShipTo(draftData.shipTo || { name: "", address: "" });
      setInvoiceDate(draftData.invoiceDate || new Date().toISOString().split('T')[0]);
      setValidUntil(draftData.validUntil || '');
      setItems(draftData.items || [{ description: "", unitPrice: 0, quantity: 0, discount: 0, tax: 0, total: 0, isLocked: false }]);
      setAdditionalNotes(draftData.additionalNotes || invoiceDefaultAdditionalNotes || "");
      setTermsAndConditions(draftData.termsAndConditions || invoiceDefaultTermsAndConditions || 'No terms and conditions specified.');
      setCoveringLetter(draftData.coveringLetter || "");
      setAuthorizedSignature(draftData.authorizedSignature || invoiceDefaultAuthorizedSignature || "");
      setAuthorizedSignatureName(draftData.authorizedSignatureName || invoiceDefaultAuthorizedSignatureName || "");
      setCustomerSignature(draftData.customerSignature || "");
      setCustomerSignatureName(draftData.customerSignatureName || "");
      setInvoiceId(draftData.firestoreId || null);
      setTemplateId(draftData.templateId || 6);
      setIsSaved(true);
      setHasUnsavedChanges(false);
      setInvoiceType(draftData.invoiceType || "proforma");
      if (editorRef.current) editorRef.current.innerHTML = draftData.coveringLetter || '';
      return; 
    }

    // Priority 2: Restore working draft from localStorage (most reliable for navigation).
    if (!InvoiceId) { // Only restore if it's a new invoice
        let draftToLoad = null;
        try {
            const storedDraft = localStorage.getItem('invoiceDraft');
            if (storedDraft) {
                const parsedDraft = JSON.parse(storedDraft);
                if (parsedDraft && typeof parsedDraft === 'object') {
                    draftToLoad = parsedDraft;
                }
            }
        } catch (error) {
            console.error("Failed to load draft from localStorage", error);
            localStorage.removeItem('invoiceDraft'); // Clear corrupted data
        }

        if (draftToLoad) {
            setInvoiceNumber(draftToLoad.invoiceNumber || "YYYYMMDD-###");
            setBillTo(draftToLoad.billTo || { name: "", address: "" });
            setShipTo(draftToLoad.shipTo || { name: "", address: "" });
            setInvoiceDate(draftToLoad.invoiceDate || new Date().toISOString().split('T')[0]);
            setValidUntil(draftToLoad.validUntil || '');
            setItems(draftToLoad.items && draftToLoad.items.length > 0 ? draftToLoad.items : [{ description: "", unitPrice: 0, quantity: 0, discount: 0, tax: 0, total: 0, isLocked: false }]);
            setAdditionalNotes(draftToLoad.additionalNotes || invoiceDefaultAdditionalNotes || "");
            setTermsAndConditions(draftToLoad.termsAndConditions || invoiceDefaultTermsAndConditions || 'No terms and conditions specified.');
            setCoveringLetter(draftToLoad.coveringLetter || "");
            setAuthorizedSignature(draftToLoad.authorizedSignature || invoiceDefaultAuthorizedSignature || "");
            setAuthorizedSignatureName(draftToLoad.authorizedSignatureName || invoiceDefaultAuthorizedSignatureName || "");
            setCustomerSignature(draftToLoad.customerSignature || "");
            setCustomerSignatureName(draftToLoad.customerSignatureName || "");
            setInvoiceType(draftToLoad.invoiceType || "tax");
            if (editorRef.current) editorRef.current.innerHTML = draftToLoad.coveringLetter || '';
        }
    }
  }, [location.state, InvoiceId, invoiceDefaultAdditionalNotes, invoiceDefaultTermsAndConditions, invoiceDefaultAuthorizedSignature, invoiceDefaultAuthorizedSignatureName]);

  // Handle selected credit notes from SelectCreditNotes page
  useEffect(() => {
    if (location.state && location.state.selectedCreditNotes && location.state.selectedCreditNotes.length > 0) {
      setSelectedCreditNotes(location.state.selectedCreditNotes);
      setTotalCreditNoteAmount(location.state.totalCreditNoteAmount || 0);
      setHasUnsavedChanges(true);
    }
  }, []);

  useEffect(() => {
    if (selectedItems && selectedItems.length > 0) {
      const newItemsFromSelection = selectedItems.map(item => {
        const quantity = parseFloat(item.moq || item.quantity || 1);
        const unitPrice = parseFloat(item.price) || 0;
        const tax = parseFloat(item.gst) || 0;
        const discount = parseFloat(item.discount) || 0;

        const discountedPrice = unitPrice * (1 - (discount / 100));
        const total = quantity * discountedPrice * (1 + (tax / 100));

        return {
          description: item.name || '',
          unitPrice,
          // preserve cost (if product provides it) so invoice can show loss warnings
          cost: item.cost !== undefined ? Number(item.cost) : (item.costPrice !== undefined ? Number(item.costPrice) : 0),
          quantity,
          discount,
          tax,
          total,
          isLocked: true,
        };
      });

      setItems(prevItems => {
        if (
          prevItems.length === 1 &&
          !prevItems[0].description &&
          prevItems[0].quantity === 0 &&
          prevItems[0].unitPrice === 0
        ) {
          return [...newItemsFromSelection];
        }
        const existingDescriptions = new Set(
          prevItems.filter(item => item.description).map(item => item.description.trim().toLowerCase())
        );
        const itemsToAdd = newItemsFromSelection.filter(
          newItem => !existingDescriptions.has((newItem.description || '').trim().toLowerCase())
        );
        return [...prevItems, ...itemsToAdd];
      });

      if (clearSelectedItems) {
        clearSelectedItems();
      }
      setHasUnsavedChanges(true);
    }
  }, [selectedItems, clearSelectedItems]);

  useEffect(() => {
    if (invoiceDate) {
        const date = new Date(invoiceDate);
        const days = parseInt(invoiceValidityDays, 10) || 7;
        date.setDate(date.getDate() + days);
        setValidUntil(date.toISOString().split('T')[0]);
        setHasUnsavedChanges(true);
    }
  }, [invoiceDate, invoiceValidityDays]);

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    const currentItem = { ...updatedItems[index], [field]: value };
  
    const quantity = parseFloat(currentItem.quantity) || 0;
    const unitPrice = parseFloat(currentItem.unitPrice) || 0;
    const discount = parseFloat(currentItem.discount) || 0;
    const tax = parseFloat(currentItem.tax) || 0;
  
    const discountedPrice = unitPrice * (1 - (discount / 100));
    const finalTotal = quantity * discountedPrice * (1 + (tax / 100));
  
    updatedItems[index] = { ...currentItem, total: finalTotal };
  
    setItems(updatedItems);
    setHasUnsavedChanges(true);
  };

 const addItem = () => {
  setItems([...items, { description: "", unitPrice: 0, quantity: 0, discount: 0, tax: 0, total: 0, isLocked: false, sourceId: null }]);
  setHasUnsavedChanges(true);
};

 const deleteItem = (index) => {
  if (items.length <= 1) {
    setItems([{ description: "", unitPrice: 0, quantity: 0, discount: 0, tax: 0, total: 0, isLocked: false, sourceId: null }]);
  } else {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  }
  setHasUnsavedChanges(true);
};
  
  const execCommand = (command) => document.execCommand(command, false, null);
  const handleEditorInput = () => {
    if (editorRef.current) {
        setCoveringLetter(editorRef.current.innerHTML);
        setHasUnsavedChanges(true);
    }
  };


  const totals = React.useMemo(() => {
    let subtotal = 0;
    let totalDiscountAmount = 0;
    let totalTaxAmount = 0;

    items.forEach(item => {
        const quantity = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.unitPrice) || 0;
        const discountPercent = parseFloat(item.discount) || 0;
        const taxPercent = parseFloat(item.tax) || 0;

        const lineSubtotal = quantity * unitPrice;
        const lineDiscountAmount = lineSubtotal * (discountPercent / 100);
        const taxableAmountForLine = lineSubtotal - lineDiscountAmount;
        const lineTaxAmount = taxableAmountForLine * (taxPercent / 100);

        subtotal += lineSubtotal;
        totalDiscountAmount += lineDiscountAmount;
        totalTaxAmount += lineTaxAmount;
    });

    const taxableAmount = subtotal - totalDiscountAmount;
    let grandTotal = taxableAmount + totalTaxAmount;
    
    // Apply credit note deduction
    const creditNoteAmount = totalCreditNoteAmount || 0;
    const netTotal = grandTotal - creditNoteAmount;

    return {
      subtotal: subtotal,
      totalDiscountAmount: totalDiscountAmount,
      taxableAmount: taxableAmount,
      totalTaxAmount: totalTaxAmount,
      grandTotal: grandTotal,
      creditNoteAmount: creditNoteAmount,
      netTotal: Math.max(0, netTotal), // Ensure no negative totals
      cgst: totalTaxAmount / 2,
      sgst: totalTaxAmount / 2,
      igst: totalTaxAmount
    };
  }, [items, totalCreditNoteAmount]);

  const generateinvoiceId = (prefix, nextNumber, type) => {
    if (type === 'proforma') {
      // Determine digit length from custom counter string
      const digitLength = invoiceCustomCounter.length || 5;
      return `Proforma-${String(nextNumber).padStart(digitLength, '0')}`;
    }
    const safePrefix = prefix.padEnd(4, 'X').slice(0, 4).toUpperCase();
    const digitLength = invoiceCustomCounter.length || 5;
    return safePrefix + '-' + String(nextNumber).padStart(digitLength, '0');
  };

  useEffect(() => {
    if (!isSaved) {
      const digitLength = invoiceCustomCounter.length || 5;
      const hashmarks = '#'.repeat(digitLength);
      if (invoiceType === 'proforma') {
        setInvoiceNumber(`Proforma-${hashmarks}`);
      } else {
        const prefix = (invoiceIdPrefix || '').padEnd(4, 'X').slice(0, 4).toUpperCase();
        setInvoiceNumber(prefix ? prefix + '-' + hashmarks : hashmarks);
      }
    }
  }, [invoiceIdPrefix, invoiceDate, isSaved, invoiceType, invoiceCustomCounter]);

  // --- CHANGE 4: UPDATED RESET FUNCTION ---
  const resetForm = () => {
    setBillTo({ name: '', address: '' });
    setShipTo({ name: '', address: '' });
    setAdditionalNotes(invoiceDefaultAdditionalNotes || '');
    setTermsAndConditions(invoiceDefaultTermsAndConditions || '');
    setInvoiceDate(new Date().toISOString().split('T')[0]);
    setCoveringLetter("");
    if(editorRef.current) editorRef.current.innerHTML = "";
    setAuthorizedSignature(invoiceDefaultAuthorizedSignature || "");
    setAuthorizedSignatureName(invoiceDefaultAuthorizedSignatureName || "");
    setCustomerSignature("");
    setCustomerSignatureName("");
    setInvoiceNumber("YYYYMMDD-###");
    setHasUnsavedChanges(true);
    setIsSaved(false);
    setInvoiceId(null);
    // Clear the saved draft from storage
    localStorage.removeItem('invoiceDraft');
  };
  
  // --- CHANGE 1: UPDATED HANDLECLICK TO SAVE STATE ---
  const handleClick = () => {
    const currentDraft = {
      invoiceNumber,
      billTo,
      shipTo,
      invoiceDate,
      validUntil,
      items,
      additionalNotes,
      termsAndConditions,
      coveringLetter,
      authorizedSignature,
      authorizedSignatureName,
      customerSignature,
      customerSignatureName,
      invoiceType,
    };
    
    // Save to context (if available)
    if (typeof setDraft === 'function') {
      setDraft(currentDraft);
    }
    
    // Also save to localStorage to ensure it persists across navigation
    try {
        localStorage.setItem('invoiceDraft', JSON.stringify(currentDraft));
    } catch (error) {
        console.error("Could not save draft to localStorage", error);
    }
    
    navigate("/select-invoice-products", { state: { from: window.location.pathname } });
  };

  const openSignatureModal = (type) => {
    setCurrentSignatureType(type);
    setShowSignatureModal(true);
  };
  const handleCloseSignatureModal = () => {
    setShowSignatureModal(false);
    setCurrentSignatureType("");
  };
  const addSignature = (signature) => {
    if (currentSignatureType === "authorized") {
      setAuthorizedSignature(signature);
    } else if (currentSignatureType === "customer") {
      setCustomerSignature(signature);
    }
    handleCloseSignatureModal();
    setHasUnsavedChanges(true);
  };

  // Helper function to get financial year (FY format: 24-25, 25-26, etc.)
  const getFinancialYear = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-11, where 0=Jan, 3=Apr
    
    // Financial year starts from April (month 3)
    if (currentMonth >= 3) {
      // April onwards: current FY
      const startYear = currentYear % 100; // Last 2 digits
      const endYear = (currentYear + 1) % 100;
      return `${String(startYear).padStart(2, '0')}-${String(endYear).padStart(2, '0')}`;
    } else {
      // Jan to March: previous FY
      const endYear = currentYear % 100;
      const startYear = (currentYear - 1) % 100;
      return `${String(startYear).padStart(2, '0')}-${String(endYear).padStart(2, '0')}`;
    }
  };

  const handleCreditNotesConfirm = (creditNotes) => {
    if (creditNotes && creditNotes.length > 0) {
      setSelectedCreditNotes(creditNotes);
      const totalAmount = creditNotes.reduce((sum, cn) => {
        return sum + (cn.totals?.grandTotal || 0);
      }, 0);
      setTotalCreditNoteAmount(totalAmount);
      setHasUnsavedChanges(true);
    }
  };

  const removeCreditNote = (index) => {
    const updatedCreditNotes = selectedCreditNotes.filter((_, idx) => idx !== index);
    setSelectedCreditNotes(updatedCreditNotes);
    const totalAmount = updatedCreditNotes.reduce((sum, cn) => {
      return sum + (cn.totals?.grandTotal || 0);
    }, 0);
    setTotalCreditNoteAmount(totalAmount);
    setHasUnsavedChanges(true);
    showNotification('Credit note removed successfully', 'success');
  };

  // Generate sequential credit note reference numbers
  const generateCreditNoteReferences = async () => {
    if (selectedCreditNotes.length === 0) return {};

    try {
      const creditNoteRefs = {};
      const fy = getFinancialYear();
      
      const userDocRef = doc(db, "users", userId);
      
      let creditNoteCounter = 0;
      
      // Get current counter in a transaction
      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userDocRef);
        
        if (userDoc.exists()) {
          creditNoteCounter = (userDoc.data()?.creditNoteRefCounter || 0) + selectedCreditNotes.length;
          if (creditNoteCounter > 99999) {
            throw new Error("Credit note reference counter exceeded maximum limit (99999)");
          }
          // Update counter to mark these numbers as used
          transaction.update(userDocRef, { creditNoteRefCounter: creditNoteCounter });
        } else {
          creditNoteCounter = selectedCreditNotes.length;
          transaction.set(userDocRef, { creditNoteRefCounter: creditNoteCounter }, { merge: true });
        }
      });
      
      // Generate reference for each credit note
      selectedCreditNotes.forEach((cn, index) => {
        const sequenceNumber = creditNoteCounter - selectedCreditNotes.length + index + 1;
        creditNoteRefs[cn.id] = `CN-${fy}-${String(sequenceNumber).padStart(5, '0')}`;
      });
      
      return creditNoteRefs;
    } catch (error) {
      console.error("Error generating credit note references:", error);
      showNotification("Error generating credit note references. Please try again.", 'error');
      return {};
    }
  };

  const saveinvoice = async () => {
    // Check if prefix or counter hasn't been set for more than 1 year
    const oneYearInMs = 365 * 24 * 60 * 60 * 1000;
    const now = new Date();
    
    // Check prefix
    if (invoicePrefixSetAt) {
      const prefixSetDate = new Date(invoicePrefixSetAt);
      if (now - prefixSetDate > oneYearInMs) {
        showNotification("⚠️ Your invoice prefix hasn't been updated in over 1 year. Please contact the administrator.", 'warning');
        return;
      }
    }
    
    // Check counter
    if (!invoiceCustomCounter) {
      showNotification("⚠️ Invoice counter ID has not been set. Please contact the administrator.", 'warning');
      return;
    }
    
    if (!billTo.name || !billTo.address || !invoiceDate || !validUntil || items.length === 0) {
      showNotification("Please fill in all required fields (Client Name, Address, Dates) and add at least one item.", 'warning');
      return;
    }
    const allItemsFilled = items.every(item => item.description && item.quantity > 0 && item.unitPrice >= 0);
    if (!allItemsFilled) {
      showNotification("Please ensure every item has a description and a quantity greater than zero.", 'warning');
      return;
    }
    
    // MODIFIED: Get tenantId from userProfile
    if (!userProfile || !userProfile.tenantId) {
      showNotification("Tenant ID not found. Please log in again.", 'error');
      return;
    }

    try {
      // Generate credit note references first
      const creditNoteReferences = await generateCreditNoteReferences();
      
      await runTransaction(db, async (transaction) => {
        const invoiceCountRef = doc(db, "invoices", "invoicecount");
        const invoiceCountSnap = await transaction.get(invoiceCountRef);
        let currentinvoiceNumber = 0;
        if (!invoiceCountSnap.exists()) {
          // initialize counter to 0
          transaction.set(invoiceCountRef, { invoicenumber: 0 });
          currentinvoiceNumber = 0;
        } else {
          currentinvoiceNumber = invoiceCountSnap.data().invoicenumber || 0;
        }
        const nextNumber = currentinvoiceNumber + 1;
        const newinvoiceNumber = generateinvoiceId(invoiceIdPrefix || '', nextNumber, invoiceType);
        const tenantId = userProfile.tenantId;

        // MODIFIED: Use invoice-specific variables
        const invoiceData = {
          invoiceId: newinvoiceNumber,
          user: userId,
          billTo,
          shipTo,
          invoiceDate,
          validUntil,
          items,
          ...totals,
          additionalNotes,
          termsAndConditions,
          templateId,
          currencySymbol,
          coveringLetter,
          logo: invoiceLogo || '',
          banner: invoiceBanner || '',
          authorizedSignature: authorizedSignature || '',
          authorizedSignatureName: authorizedSignatureName || '',
          customerSignature: customerSignature || '',
          customerSignatureName: customerSignatureName || '',
          invoiceFooter: invoiceFooter || '',
          updatedAt: Timestamp.now(),
          invoiceType,
          createdAt: Timestamp.now(),
          tenantId: tenantId,
          creditNoteReferences: creditNoteReferences
        };

        const invoicesCol = collection(db, "invoices");
        await addDoc(invoicesCol, invoiceData);

        transaction.update(invoiceCountRef, { invoicenumber: nextNumber });

        setInvoiceNumber(newinvoiceNumber);
        // Store generated credit note references for use in PDF generation
        localStorage.setItem(`creditNoteRefs_${newinvoiceNumber}`, JSON.stringify(creditNoteReferences));
        showNotification("Invoice saved successfully!", 'success');
        setIsSaved(true);
        setHasUnsavedChanges(false);
        // --- CHANGE 3: CLEAR DRAFT ON SUCCESSFUL SAVE ---
        localStorage.removeItem('invoiceDraft');
      });
    } catch (error) {
      console.error("Error saving Invoice/items: ", error);
      showNotification("Error saving Invoice. Please try again.", 'error');
    }
  };

  const generatePDF = () => {
    if (invoiceType === 'proforma') {
      showNotification('PDF download is not available for Proforma Invoice. Only preview is shown.', 'info');
      return;
    }
    if (!isSaved) {
      showNotification("Please save the Invoice before downloading the PDF.", 'warning');
      return;
    }

    // Check if credit note amount exceeds grand total
    const excessCreditNoteAmount = Math.max(0, totalCreditNoteAmount - totals.grandTotal);
    
    if (excessCreditNoteAmount > 0) {
      showNotification(`Excess Credit Note Detected! A new credit note will be generated for the excess amount.`, 'info');
    }

    // Retrieve credit note references from localStorage
    const storedRefs = localStorage.getItem(`creditNoteRefs_${invoiceNumber}`);
    const creditNoteReferences = storedRefs ? JSON.parse(storedRefs) : {};
    
    // MODIFIED: Use invoice-specific variables
    // MODIFIED: Use invoice-specific variables from context
    generateInvoicePDF({
      businessName: invoiceBusinessName,
      businessAddress: invoiceBusinessAddress,
      logo: invoiceLogo,
      banner: invoiceBanner,
      invoiceNumber,
      invoiceDate,
      validUntil,
      billTo,
      shipTo,
      items,
      totals,
      currencySymbol,
      additionalNotes,
      termsAndConditions,
      coveringLetter,
      authorizedSignature,
      authorizedSignatureName,
      customerSignature,
      customerSignatureName,
      invoiceFooter: invoiceFooter,
      isSaved,
      setIsPrinting,
      invoiceType,
      selectedCreditNotes,
      userId,
      excessCreditNoteAmount, // Pass excess amount for PDF generation
      creditNoteReferences: creditNoteReferences, // Pass generated reference numbers
      showGST: showInvoiceGST, // Pass GST toggle flag
      summary: [
        { label: 'Subtotal', value: formatCurrency(totals.subtotal, currencySymbol === '₹' ? 'INR' : currencySymbol) },
        { label: 'Discount', value: '- ' + formatCurrency(totals.totalDiscountAmount, currencySymbol === '₹' ? 'INR' : currencySymbol) },
        { label: 'Taxable Amt', value: formatCurrency(totals.taxableAmount, currencySymbol === '₹' ? 'INR' : currencySymbol) },
        ...(showInvoiceGST ? [
          { label: 'CGST', value: formatCurrency(totals.cgst, currencySymbol === '₹' ? 'INR' : currencySymbol) },
          { label: 'SGST', value: formatCurrency(totals.sgst, currencySymbol === '₹' ? 'INR' : currencySymbol) },
          { label: 'IGST', value: formatCurrency(totals.igst, currencySymbol === '₹' ? 'INR' : currencySymbol) },
          { label: 'Total Tax', value: formatCurrency(totals.totalTaxAmount, currencySymbol === '₹' ? 'INR' : currencySymbol) },
        ] : []),
        ...(totalCreditNoteAmount > 0 ? [
          { label: 'Grand Total', value: formatCurrency(totals.grandTotal, currencySymbol === '₹' ? 'INR' : currencySymbol) },
          { label: 'Less: Credit Notes', value: '- ' + formatCurrency(totals.creditNoteAmount, currencySymbol === '₹' ? 'INR' : currencySymbol) },
          ...(excessCreditNoteAmount > 0 ? [
            { label: 'Excess Credit Note Amount', value: formatCurrency(excessCreditNoteAmount, currencySymbol === '₹' ? 'INR' : currencySymbol) },
          ] : []),
          { label: 'Net Total', value: formatCurrency(totals.netTotal, currencySymbol === '₹' ? 'INR' : currencySymbol) },
        ] : []),
      ],
      pdfFileName: invoiceNumber ? `${invoiceNumber}.pdf` : 'invoice.pdf',
    });
  };

  const handleGenerateNew = () => {
    resetForm();
  };

  const handleClearForm = () => {
    resetForm();
    setIsSaved(false); 
    setInvoiceId(null);
  };

  return (
    <ThemeProvider theme={theme}>
        <SignatureUploadDialog
            open={showSignatureModal}
            onClose={handleCloseSignatureModal}
            onSave={addSignature}
        />
        <SelectCreditNotesModal
            open={showCreditNotesModal}
            onClose={() => setShowCreditNotesModal(false)}
            onConfirm={handleCreditNotesConfirm}
        />
        <Box p={{ xs: 2, sm: 3 }} sx={{ backgroundColor: "#f0f4f8", pb: 12 }}>
            <Paper elevation={3} sx={{ padding: { xs: 2, sm: 4}, backgroundColor: "#fff", borderRadius: 2, boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" }}>
              <div ref={contentRef}>
                {!isPrinting &&
                  <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ mb: 2, color: "#3f51b5", fontWeight: "bold", borderBottom: "2px solid #3f51b5", paddingBottom: 1, display: "inline-block" }}>
                          Covering Letter
                      </Typography>
                      <Paper elevation={1} sx={{ mb: 1 }}>
                          <Toolbar variant="dense">
                              <ButtonGroup size="small">
                                  <Button onClick={() => execCommand("bold")}> <FormatBold /> </Button>
                                  <Button onClick={() => execCommand("italic")}> <FormatItalic /> </Button>
                                  <Button onClick={() => execCommand("underline")}> <FormatUnderlined /> </Button>
                              </ButtonGroup>
                              <Divider orientation="vertical" flexItem sx={{ mx: 1.5 }} />
                              <ButtonGroup size="small">
                                  <Button onClick={() => execCommand("insertUnorderedList")}> <FormatListBulleted /> </Button>
                                  <Button onClick={() => execCommand("insertOrderedList")}> <FormatListNumbered /> </Button>
                              </ButtonGroup>
                              <Divider orientation="vertical" flexItem sx={{ mx: 1.5 }} />
                              <ButtonGroup size="small">
                                  <Button onClick={() => execCommand("justifyLeft")}> <FormatAlignLeft /> </Button>
                                  <Button onClick={() => execCommand("justifyCenter")}> <FormatAlignCenter /> </Button>
                                  <Button onClick={() => execCommand("justifyRight")}> <FormatAlignRight /> </Button>
                                  <Button onClick={() => execCommand("justifyFull")}> <FormatAlignJustify /> </Button>
                              </ButtonGroup>
                          </Toolbar>
                      </Paper>
                      <Box
                          ref={editorRef}
                          contentEditable
                          onInput={handleEditorInput}
                          sx={{
                              minHeight: 120,
                              border: "1px solid #ccc",
                              p: 2,
                              mb: 3,
                              borderRadius: 1,
                              "&:focus": { outline: "2px solid #1976d2" },
                          }}
                          suppressContentEditableWarning
                      />
                  </Box>
                }
                
                <Grid container justifyContent="space-between" alignItems="flex-start" sx={{ mb: 4 }}>
                  {invoiceBanner ? (
                    <>
                      {/* Centered heading + toggle */}
                      <Grid item xs={12} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h4" color="primary" sx={{ fontWeight: 700, textAlign: 'center' }}>
                              {invoiceType === 'tax' ? 'TAX INVOICE' : 'PROFORMA INVOICE'}
                            </Typography>
                            <ToggleButtonGroup value={invoiceType} exclusive onChange={handleInvoiceTypeChange} aria-label="invoice type" size="small">
                              <ToggleButton value="tax">Tax</ToggleButton>
                              <ToggleButton value="proforma">Proforma</ToggleButton>
                            </ToggleButtonGroup>
                          </Box>
                        </Box>
                      </Grid>
                      
                      {/* Banner full width */}
                      <Grid item xs={12}>
                        <Box sx={{ width: '100%', height: { xs: 160, sm: 200, md: 240 }, overflow: 'hidden', mb: 2, borderRadius: 1 }}>
                          <img src={invoiceBanner} alt="Company Banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                      </Grid>

                      {/* Centered single-line ID + date row below banner */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center', width: '100%', flexWrap: 'wrap', mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                label="Invoice ID"
                                value={invoiceNumber}
                                size="small"
                                sx={{ minWidth: 160, maxWidth: 280 }}
                                InputProps={{ readOnly: true }}
                              />
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                type="date"
                                label="Invoice Date"
                                value={invoiceDate}
                                onChange={handleInvoiceDateChange}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                sx={{ maxWidth: 200 }}
                                inputProps={{
                                  min: invoiceType === 'tax' ? getTodayDate() : undefined,
                                  max: invoiceType === 'tax' ? getTodayDate() : undefined,
                                }}
                              />
                            </Box>
                          </Box>
                      </Grid>
                    </>
                  ) : (
                    <>
                      {/* No banner: keep logo/info left and controls on right. */}
                      <Grid item xs={12} md={7}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {invoiceLogo && (
                            <Box sx={{
                              flexShrink: 0,
                              mr: 2.5,
                              width: '360px',
                              height: '240px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <img
                                src={invoiceLogo}
                                alt="Company Logo"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain'
                                }}
                              />
                            </Box>
                          )}
                          <Box>
                            <Typography variant="h6" gutterBottom sx={{ lineHeight: 1.2 }}>
                              {invoiceBusinessName}
                            </Typography>
                            <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                              {invoiceBusinessAddress}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={5} sx={{ pt: { xs: 2, md: 0 } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-end' }, gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h5" color="primary" sx={{ fontWeight: 700, textAlign: 'center' }}>
                              {invoiceType === 'tax' ? 'TAX INVOICE' : 'PROFORMA INVOICE'}
                            </Typography>
                            <ToggleButtonGroup value={invoiceType} exclusive onChange={handleInvoiceTypeChange} aria-label="invoice type" size="small">
                              <ToggleButton value="tax">Tax</ToggleButton>
                              <ToggleButton value="proforma">Proforma</ToggleButton>
                            </ToggleButtonGroup>
                          </Box>

                          {/* ID and date on one line */}
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                label="Invoice ID"
                                value={invoiceNumber}
                                size="small"
                                sx={{ maxWidth: 200 }}
                                InputProps={{ readOnly: true }}
                              />
                            </Box>

                            <TextField
                              type="date"
                              label="Invoice Date"
                              value={invoiceDate}
                              onChange={handleInvoiceDateChange}
                              InputLabelProps={{ shrink: true }}
                              size="small"
                              sx={{ maxWidth: 200 }}
                              inputProps={{
                                min: invoiceType === 'tax' ? getTodayDate() : undefined,
                                max: invoiceType === 'tax' ? getTodayDate() : undefined,
                              }}
                            />
                          </Box>

                          {/* Valid Until remains on its own line */}
                          <Box sx={{ mt: 1 }}>
                            <TextField
                              type="date"
                              label="Valid Until"
                              value={validUntil}
                              onChange={(e) => { setValidUntil(e.target.value); setHasUnsavedChanges(true); }}
                              InputLabelProps={{ shrink: true }}
                              size="small"
                              sx={{ maxWidth: 200 }}
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
                
                <Divider sx={{ my: 3, borderColor: "primary.main" }} />
                
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ backgroundColor: "#ffffff", border: "1px solid #e0e0e0", p: 2, borderRadius: 1 }}>
                            <Typography variant="h6" sx={{ mb: 1, color: "primary.main" }}>Bill To:</Typography>
                            <TextField fullWidth variant="outlined" value={billTo.name}
                                onChange={(e) => { setBillTo((prev) => ({ ...prev, name: e.target.value })); setHasUnsavedChanges(true); }}
                                placeholder="Client Name" sx={{ mb: 2 }}
                            />
                            <TextField fullWidth multiline rows={3} variant="outlined" value={billTo.address}
                                onChange={(e) => { setBillTo((prev) => ({ ...prev, address: e.target.value })); setHasUnsavedChanges(true); }}
                                placeholder="Client Address"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ backgroundColor: "#ffffff", border: "1px solid #e0e0e0", p: 2, borderRadius: 1 }}>
                            <Typography variant="h6" sx={{ mb: 1, color: "primary.main" }}>Ship To:</Typography>
                            <TextField fullWidth variant="outlined" value={shipTo.name}
                                onChange={(e) => { setShipTo((prev) => ({ ...prev, name: e.target.value })); setHasUnsavedChanges(true); }}
                                placeholder="Shipping Name" sx={{ mb: 2 }}
                            />
                            <TextField fullWidth multiline rows={3} variant="outlined" value={shipTo.address}
                                onChange={(e) => { setShipTo((prev) => ({ ...prev, address: e.target.value })); setHasUnsavedChanges(true); }}
                                placeholder="Shipping Address"
                            />
                        </Box>
                    </Grid>
                </Grid>
                
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleClick}
                        sx={{ py: 1.5, px: 3 }}
                    >
                        + Select Items
                    </Button>
                    {!isPrinting && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={addItem}
                            className="hide-on-print"
                        >
                            Add Manual Item Row
                        </Button>
                    )}
                    {!isPrinting && (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => setShowCreditNotesModal(true)}
                            className="hide-on-print"
                        >
                            Select Credit Notes
                        </Button>
                    )}
                </Box>

                <TableContainer component={Paper} elevation={2} sx={{ mb: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Product Name</TableCell>
                        <TableCell align="left" sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Quantity</TableCell>
                        <TableCell align="left" sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Unit Price</TableCell>
                        <TableCell align="left" sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Discount (%)</TableCell>
                        {showInvoiceGST && (
                          <TableCell align="left" sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Tax (%)</TableCell>
                        )}
                        <TableCell align="left" sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Total</TableCell>
                        {!isPrinting && (
                          <TableCell align="left" sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Actions</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    {items.map((item, index) => (
                        <TableRow key={index}>
                            <TableCell sx={{p:1}}>
                                <TextField
                                    label="Product Name"
                                    value={item.description}
                                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                    fullWidth
                                    required
                                    InputProps={{ readOnly: item.isLocked }}
                                    size="small"
                                />
                            </TableCell>
                            <TableCell sx={{p:1}}>
                            <TextField
                                label="Quantity"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                fullWidth
                                type="number"
                                size="small"
                                InputProps={{ readOnly: item.isLocked }}
                            />
                            </TableCell>
                            <TableCell sx={{p:1}}>
                            <TextField
                                label="Unit Price"
                                value={item.unitPrice}
                                onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                                fullWidth
                                type="number"
                                size="small"
                                InputProps={{ 
                                    readOnly: item.isLocked,
                                    startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> 
                                }}
                            />
                            </TableCell>
                            <TableCell sx={{p:1}}>
                            <TextField
                                label="Discount"
                                value={item.discount}
                                onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                                fullWidth
                                type="number"
                                size="small"
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment> 
                                }}
                            />
                            </TableCell>
                            {showInvoiceGST && (
                              <TableCell sx={{p:1}}>
                              <TextField
                                  label="Tax"
                                  value={item.tax}
                                  onChange={(e) => handleItemChange(index, 'tax', e.target.value)}
                                  fullWidth
                                  type="number"
                                  size="small"
                                  InputProps={{ 
                                      readOnly: item.isLocked,
                                      endAdornment: <InputAdornment position="end">%</InputAdornment> 
                                  }}
                              />
                              </TableCell>
                            )}
                            <TableCell sx={{p:1}}>
                            <TextField
                                label="Total"
                                value={item.total.toFixed(2)}
                                fullWidth
                                disabled
                                size="small"
                                InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }}
                            />
                            </TableCell>
              {!isPrinting &&
                <TableCell sx={{p:1, display: 'flex', alignItems: 'center', gap: 1}}>
                      {(() => {
                        const unitPrice = parseFloat(item.unitPrice) || 0;
                        const discount = parseFloat(item.discount) || 0;
                        const quantity = parseFloat(item.quantity) || 0;
                        const discountedUnit = unitPrice * (1 - (discount/100));
                        const subtotalAfterDiscount = discountedUnit * quantity;
                        const costVal = parseFloat(item.cost) || 0;
                        const totalCost = costVal * quantity;
                        const isLoss = costVal > 0 && subtotalAfterDiscount < totalCost;
                        return (
                          <React.Fragment>
                            <IconButton color="secondary" onClick={() => deleteItem(index)}>
                              <DeleteIcon />
                            </IconButton>
                            {isLoss && (
                              <Tooltip title={`Warning: Selling after discount (subtotal): ${formatCurrency(subtotalAfterDiscount, currencySymbol)} — Total cost: ${formatCurrency(totalCost, currencySymbol)}`}>
                                <WarningAmberIcon sx={{ color: 'warning.main', ml: 0.5 }} fontSize="small" />
                              </Tooltip>
                            )}
                          </React.Fragment>
                        );
                      })()}
                </TableCell>
              }
                        </TableRow>
                    ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Display Selected Credit Notes */}
                {selectedCreditNotes.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#f57c00', fontWeight: 'bold' }}>
                      Credit Notes Applied
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Credit Note #</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedCreditNotes.map((cn, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{cn.creditNoteNumber}</TableCell>
                              <TableCell>{cn.billTo?.name}</TableCell>
                              <TableCell align="right">{formatCurrency(cn.totals?.grandTotal || 0, currencySymbol)}</TableCell>
                              <TableCell align="center">
                                <Tooltip title="Remove Credit Note">
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => removeCreditNote(idx)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}>
                    <Box sx={{ width: '280px' }}>
                        <Grid container justifyContent="space-between">
                            <Grid item>Subtotal:</Grid>
                            <Grid item>{formatCurrency(totals.subtotal, currencySymbol)}</Grid>
                        </Grid>
                        {showInvoiceGST && (
                          <Grid container justifyContent="space-between">
                              <Grid item>Total GST:</Grid>
                              <Grid item>{formatCurrency(totals.totalTaxAmount, currencySymbol)}</Grid>
                          </Grid>
                        )}
                        <Grid container justifyContent="space-between" sx={{ fontWeight: "bold", mt: 1 }}>
                            <Grid item>Grand Total:</Grid>
                            <Grid item>{formatCurrency(totals.grandTotal, currencySymbol)}</Grid>
                        </Grid>
                        {totalCreditNoteAmount > 0 && (
                          <>
                            <Grid container justifyContent="space-between" sx={{ color: '#f57c00', mt: 1 }}>
                              <Grid item>Less: Credit Notes:</Grid>
                              <Grid item>- {formatCurrency(totals.creditNoteAmount, currencySymbol)}</Grid>
                            </Grid>
                            <Grid container justifyContent="space-between" sx={{ fontWeight: "bold", mt: 1, pt: 1, borderTop: '2px solid #ddd', color: '#1976d2' }}>
                              <Grid item>Net Total:</Grid>
                              <Grid item>{formatCurrency(totals.netTotal, currencySymbol)}</Grid>
                            </Grid>
                          </>
                        )}
                    </Box>
                </Box>
                
                <Typography variant="h6" sx={{ mt: 2 }}>Summary:</Typography>
                <TableContainer component={Paper} sx={{ p: 2, mb: 2 }}>
                    <Table>
                        <TableBody>
                            <TableRow sx={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(120px, 1fr))`, gap: 2, "& .MuiTableCell-root": { display: "flex", flexDirection: "column", alignItems: "start", padding: "12px", border: 'none' }}}>
                                <TableCell><Typography variant="subtitle2" color="textSecondary">Subtotal</Typography><Typography variant="h6" sx={{ mt: 1 }}>{formatCurrency(totals.subtotal, currencySymbol)}</Typography></TableCell>
                                <TableCell><Typography variant="subtitle2" color="textSecondary">Discount</Typography><Typography variant="h6" sx={{ mt: 1 }}>- {formatCurrency(totals.totalDiscountAmount, currencySymbol)}</Typography></TableCell>
                                <TableCell><Typography variant="subtitle2" color="textSecondary">Taxable Amt</Typography><Typography variant="h6" sx={{ mt: 1 }}>{formatCurrency(totals.taxableAmount, currencySymbol)}</Typography></TableCell>
                                {showInvoiceGST && (
                                  <>
                                    <TableCell><Typography variant="subtitle2" color="textSecondary">CGST</Typography><Typography variant="h6" sx={{ mt: 1 }}>{formatCurrency(totals.cgst, currencySymbol)}</Typography></TableCell>
                                    <TableCell><Typography variant="subtitle2" color="textSecondary">SGST</Typography><Typography variant="h6" sx={{ mt: 1 }}>{formatCurrency(totals.sgst, currencySymbol)}</Typography></TableCell>
                                    <TableCell><Typography variant="subtitle2" color="textSecondary">IGST</Typography><Typography variant="h6" sx={{ mt: 1 }}>{formatCurrency(totals.igst, currencySymbol)}</Typography></TableCell>
                                    <TableCell><Typography variant="subtitle2" color="textSecondary">Total Tax</Typography><Typography variant="h6" sx={{ mt: 1 }}>{formatCurrency(totals.totalTaxAmount, currencySymbol)}</Typography></TableCell>
                                  </>
                                )}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

              
                <Box mb={4}>
                  <Typography variant="h6" color="primary" gutterBottom>Additional Notes</Typography>
                  <TextField label="Provide any additional notes here" fullWidth multiline rows={4} value={additionalNotes} onChange={(e) => {setAdditionalNotes(e.target.value); setHasUnsavedChanges(true);}} variant="outlined" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, padding: '10px' }, whiteSpace: 'pre-line' }}/>
                </Box>

                <Box mb={4}>
                  <Typography variant="h6" color="primary" gutterBottom>Terms and Conditions</Typography>
                    <TextField fullWidth multiline rows={4} variant="outlined" value={termsAndConditions} onChange={(e) => { setTermsAndConditions(e.target.value); setHasUnsavedChanges(true);}} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, padding: '10px' }, whiteSpace: 'pre-line' }}/>
                </Box>
                
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" color="primary" gutterBottom>Signatures</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'primary.main', fontSize: '0.95rem' }}>Authorized Signature</Typography>
                                <Paper elevation={1} sx={{ p: 1.5, border: "1px solid #ccc", borderRadius: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '80px' }}>
                                      {authorizedSignature && authorizedSignature !== "" ? (
                                      <Box sx={{ position: "relative", width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                          <img src={authorizedSignature} alt="Authorized Signature" style={{ width: "100%", maxHeight: 60, border: '1px solid #eee', objectFit: 'contain' }} />
                                          {!isPrinting && 
                                          <IconButton size="small" sx={{ position: "absolute", top: 0, right: 0 }} onClick={() => { setAuthorizedSignature(null); setAuthorizedSignatureName(""); setHasUnsavedChanges(true); }}>
                                              <Close fontSize="small" />
                                          </IconButton>
                                          }
                                      </Box>
                                      ) : (
                                      <Box sx={{ textAlign: 'center', py: 1 }}>
                                        <CloudUploadIcon sx={{ fontSize: 36, color: '#bbb', mb: 0.5 }} />
                                        <Typography variant="body2" sx={{ color: '#666', mb: 1, fontSize: '0.85rem' }}>
                                          Upload Signature Image
                                        </Typography>
                                        <Button
                                          variant="contained"
                                          component="label"
                                          startIcon={<CloudUploadIcon />}
                                          sx={{ mb: 0.5 }}
                                          size="small"
                                        >
                                          CHOOSE IMAGE
                                          <input
                                            hidden
                                            accept="image/jpeg,image/png"
                                            type="file"
                                            onChange={(e) => {
                                              const file = e.target.files[0];
                                              if (file) openSignatureModal("authorized");
                                            }}
                                          />
                                        </Button>
                                        <Typography variant="caption" sx={{ display: 'block', color: '#999', fontSize: '0.75rem' }}>
                                          JPEG, PNG | Max 150KB
                                        </Typography>
                                      </Box>
                                      )}
                                    </Box>
                                </Paper>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'primary.main', fontSize: '0.95rem' }}>Customer Signature (Optional)</Typography>
                                <Paper elevation={1} sx={{ p: 1.5, border: "1px solid #ccc", borderRadius: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '80px' }}>
                                      {customerSignature && customerSignature !== "" ? (
                                      <Box sx={{ position: "relative", width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                          <img src={customerSignature} alt="Customer Signature" style={{ width: "100%", maxHeight: 60, border: '1px solid #eee', objectFit: 'contain' }} />
                                          {!isPrinting && 
                                          <IconButton size="small" sx={{ position: "absolute", top: 0, right: 0 }} onClick={() => { setCustomerSignature(""); setCustomerSignatureName(""); setHasUnsavedChanges(true); }}>
                                              <Close fontSize="small" />
                                          </IconButton>
                                          }
                                      </Box>
                                      ) : (
                                      <Box sx={{ textAlign: 'center', py: 1 }}>
                                        <CloudUploadIcon sx={{ fontSize: 36, color: '#bbb', mb: 0.5 }} />
                                        <Typography variant="body2" sx={{ color: '#666', mb: 1, fontSize: '0.85rem' }}>
                                          Upload Signature Image
                                        </Typography>
                                        <Button
                                          variant="contained"
                                          component="label"
                                          startIcon={<CloudUploadIcon />}
                                          sx={{ mb: 0.5 }}
                                          size="small"
                                        >
                                          CHOOSE IMAGE
                                          <input
                                            hidden
                                            accept="image/jpeg,image/png"
                                            type="file"
                                            onChange={(e) => {
                                              const file = e.target.files[0];
                                              if (file) openSignatureModal("customer");
                                            }}
                                          />
                                        </Button>
                                        <Typography variant="caption" sx={{ display: 'block', color: '#999', fontSize: '0.75rem' }}>
                                          JPEG, PNG | Max 150KB
                                        </Typography>
                                      </Box>
                                      )}
                                    </Box>
                                </Paper>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {invoiceFooter && (
                  <Box mb={4} mt={4} sx={{ backgroundColor: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                    <Typography variant="h6" color="primary" gutterBottom></Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>{invoiceFooter}</Typography>
                  </Box>
                )}

              </div>
            </Paper>

            <Grid container spacing={2} sx={{ mt: 4, mb: 8, position: 'relative', zIndex: 10 }} justifyContent="center">
                <Grid item xs={12} sm={6} md={3}>
                    <Button variant="contained" color="primary" fullWidth onClick={saveinvoice} sx={{ borderRadius: 2, padding: "10px 0" }} disabled={!hasUnsavedChanges && isSaved}>
                        {isSaved ? 'Save Changes' : 'Save Invoice'}
                    </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    onClick={generatePDF}
                    sx={{ borderRadius: 2, padding: "10px 0" }}
                    disabled={!isSaved || isPrinting || invoiceType === 'proforma'}
                  >
                    {isPrinting ? 'Generating...' : 'Download PDF'}
                  </Button>
                </Grid>
                {isSaved && (
                    <Grid item xs={12} sm={6} md={3}>
                        <Button variant="outlined" color="primary" fullWidth onClick={handleGenerateNew} startIcon={<RefreshIcon />} sx={{ borderRadius: 2, padding: "10px 0" }}>
                            Generate New
                        </Button>
                    </Grid>
                )}
                <Grid item xs={12} sm={6} md={3}>
                     <Button variant="outlined" color="secondary" fullWidth onClick={handleClearForm} startIcon={<ClearAllIcon />} sx={{ borderRadius: 2, padding: "10px 0" }}>
                        Clear Form
                    </Button>
                </Grid>
            </Grid>
        </Box>
        <Snackbar
          open={notification.open}
          autoHideDuration={2500}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseNotification} severity={notification.severity}>
            {notification.message}
          </Alert>
        </Snackbar>
    </ThemeProvider>
  );
  
};

export default InTemplate;